import React, { useEffect, useState } from 'react';
import SideModal from 'src/components/SideModal';
import ModalHeader from 'src/components/ModalHeader';
import ModalFooter from 'src/components/ModalFooter';
import Input from 'src/components/Input';
import Flex from 'src/components/Flex';
import { useCreatePrompt, useUpdatePrompt, AiPrompt } from 'src/queries/useAiPrompts';
import { ToastStoreObject, ToastType } from 'src/stores/ToastStore';
import { parseError } from 'src/utils';
import styles from './styles.module.scss';

interface EditAiPromptModalProps {
    isOpen: boolean;
    onClose: () => void;
    prompt?: AiPrompt | null;
    mode: 'create' | 'edit';
}

function EditAiPromptModal({ isOpen, onClose, prompt, mode }: EditAiPromptModalProps) {
    const createPromptMutation = useCreatePrompt();
    const updatePromptMutation = useUpdatePrompt();
    const [promptData, setPromptData] = useState({
        promptName: '',
        promptDescription: '',
        promptText: '',
        promptType: 'encounter_scan' as const,
    });
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    const isLoading = createPromptMutation.isLoading || updatePromptMutation.isLoading;

    useEffect(() => {
        if (isOpen) {
            if (mode === 'edit' && prompt) {
                const { promptName, promptDescription, promptText } = prompt;
                setPromptData({
                    promptName,
                    promptDescription: promptDescription || '',
                    promptText,
                    promptType: 'encounter_scan',
                });
            } else {
                setPromptData({
                    promptName: '',
                    promptDescription: '',
                    promptText: '',
                    promptType: 'encounter_scan',
                });
            }
            setErrors({});
        }
    }, [isOpen, mode, prompt]);

    const validateForm = () => {
        const newErrors: { [key: string]: string } = {};

        if (!promptData.promptName.trim()) {
            newErrors.promptName = 'Prompt name is required';
        }

        if (!promptData.promptText.trim()) {
            newErrors.promptText = 'Prompt text is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        // Validate that we have a prompt when in edit mode
        if (mode === 'edit' && !prompt) {
            ToastStoreObject.show('Error: No prompt selected for editing', ToastType.Error);
            return;
        }

        try {
            if (mode === 'create') {
                await createPromptMutation.mutateAsync(promptData);
                ToastStoreObject.show('Prompt created successfully', ToastType.Success);
            } else {
                // At this point we know prompt exists due to validation above
                await updatePromptMutation.mutateAsync({ promptId: prompt!.promptId, promptData });
                ToastStoreObject.show('Prompt updated successfully', ToastType.Success);
            }
            onClose();
        } catch (error) {
            const errorMessage = parseError(error);
            ToastStoreObject.show(errorMessage, ToastType.Error);
        }
    };

    const handleInputChange = (field: string, value: string) => {
        setPromptData((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: '' }));
        }
    };

    return (
        <SideModal isOpen={isOpen} onClose={onClose}>
            <ModalHeader title={mode === 'create' ? 'Create Prompt' : 'Edit Prompt'} onClose={onClose} />

            <div className={styles.modalContent}>
                <Flex direction="column" className={styles.formContainer}>
                    <Input
                        label="Prompt Name"
                        value={promptData.promptName}
                        onChangeText={(value: string) => handleInputChange('promptName', value)}
                        placeholder="Enter prompt name"
                        errorMessage={errors.promptName}
                        required
                    />

                    <Input
                        label="Description"
                        value={promptData.promptDescription}
                        onChangeText={(value: string) => handleInputChange('promptDescription', value)}
                        placeholder="Enter prompt description (optional)"
                        multiLine
                        rows={3}
                    />

                    <Input
                        label="Prompt Text"
                        value={promptData.promptText}
                        onChangeText={(value: string) => handleInputChange('promptText', value)}
                        placeholder="Enter the prompt text"
                        multiLine
                        rows={25}
                        errorMessage={errors.promptText}
                        required
                    />

                    <Input label="Prompt Type" value={promptData.promptType} disabled placeholder="encounter_scan" />
                </Flex>
            </div>

            <ModalFooter
                primaryText={mode === 'create' ? 'Create' : 'Update'}
                secondaryText="Cancel"
                primaryClick={handleSubmit}
                secondaryClick={onClose}
                isLoading={isLoading}
            />
        </SideModal>
    );
}

export default EditAiPromptModal;
